/**
 * Per clinician-day facts — the one place that decides whether a day's booked
 * time comes from real visits (server snapshot, `meta.realDays`) or from the
 * virtual census, and how supply and demand attribute to territories.
 *
 * Supply lands in the territories the clinician OWNS (roster), split evenly
 * when they own more than one; demand lands in the PATIENT's territory (zip).
 * A cross-line visit therefore reads as booked in the patient's territory
 * against the owner's target — that is the "territory over-committed, drive
 * time speaks" story. Every capacity selector builds on this.
 */
import type { DateKey, DemoState, RosterEntry, ShiftLike, VisitResourceLike } from "@caredna-marlin/utils/homeHealthDemo/state";
import { dateKeyStart, addDaysToKey, isoWeekday } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { matchProvider } from "./bindings";
import { effectiveVisitResources } from "./effectiveCalendar";
import { territoryForZip } from "@caredna-marlin/utils/homeHealthDemo/engine/territory";
import { virtualDay } from "./virtualCensus";
import { isAssessmentKind, pointsFor, socVisitTypeIds, visitTypeKindFor } from "@caredna-marlin/utils/homeHealthDemo/engine/visitTypes";
import { rosterEntries, supplyTerritoriesFor } from "./roster";

export interface ClinicianDayFacts {
  handle: string;
  dateKey: DateKey;
  providerId?: number;
  hasShift: boolean;
  shiftMinutes: number;
  overflowMinutes: number;
  /** Non-filtering blocks (break, meeting) — time not available for visits. */
  blockMinutes: number;
  /** Reserved-for-assessment `visit_type` blocks — capacity earmarked for SOC/ROC/recert. */
  reservedSocBlockMinutes: number;
  isReal: boolean;
  bookedMinutes: number;
  driveMinutes: number;
  deliveredMinutes: number;
  assessments: number;
  /** Points of non-canceled visits that day. */
  points: number;
  /** Primary owned territory (first share). */
  supplyTerritory: string;
  /** Share of this clinician's supply per owned territory; shares sum to 1. */
  supplyShares: Record<string, number>;
  /** Demand minutes (booked + drive) by patient territory. */
  demandByTerritory: Record<string, number>;
  /** Points by patient territory. */
  pointsByTerritory: Record<string, number>;
}

const MINUTE = 60_000;

export function overlapMinutes(startIso: string, endIso: string, dayStart: Date, dayEnd: Date): number {
  const s = Math.max(new Date(startIso).getTime(), dayStart.getTime());
  const e = Math.min(new Date(endIso).getTime(), dayEnd.getTime());
  return e > s ? Math.round((e - s) / MINUTE) : 0;
}

export function dayBounds(dateKey: DateKey, timeZone: string): { start: Date; end: Date } {
  return { start: dateKeyStart(dateKey, timeZone), end: dateKeyStart(addDaysToKey(dateKey, 1), timeZone) };
}

export function providerIdFor(state: Pick<DemoState, "meta" | "server">, entry: RosterEntry): number | undefined {
  const bound = state.meta.bindings.providerIdsByHandle[entry.handle] ?? entry.providerId;
  if (bound !== undefined) return bound;
  return matchProvider(state.server.providers, entry)?.id;
}

export function shiftsOn(shifts: ShiftLike[], providerId: number | undefined, dateKey: DateKey, timeZone: string): ShiftLike[] {
  if (providerId === undefined) return [];
  const { start, end } = dayBounds(dateKey, timeZone);
  return shifts.filter((s) => s.providerId === providerId && s.status === "confirmed" && overlapMinutes(s.startIso, s.endIso, start, end) > 0);
}

function isSandboxAdded(state: Pick<DemoState, "sandbox">, handle: string): boolean {
  return !!state.sandbox?.addedClinicians.some((c) => c.handle === handle);
}

export function supplySharesFor(state: Pick<DemoState, "config">, entry: RosterEntry): Record<string, number> {
  const territories = supplyTerritoriesFor(entry, state.config.branch);
  const share = 1 / territories.length;
  return Object.fromEntries(territories.map((t) => [t, share]));
}

function spread(total: number, shares: Record<string, number>): Record<string, number> {
  const out: Record<string, number> = {};
  for (const [t, share] of Object.entries(shares)) out[t] = Math.round(total * share);
  return out;
}

export function clinicianDayFacts(state: DemoState, entry: RosterEntry, dateKey: DateKey): ClinicianDayFacts {
  const tz = state.config.branch.timeZone;
  const { start, end } = dayBounds(dateKey, tz);
  const providerId = providerIdFor(state, entry);
  const supplyShares = supplySharesFor(state, entry);
  const supplyTerritory = Object.keys(supplyShares)[0];
  const empty: ClinicianDayFacts = {
    handle: entry.handle,
    dateKey,
    providerId,
    hasShift: false,
    shiftMinutes: 0,
    overflowMinutes: 0,
    blockMinutes: 0,
    reservedSocBlockMinutes: 0,
    isReal: state.meta.realDays.includes(dateKey),
    bookedMinutes: 0,
    driveMinutes: 0,
    deliveredMinutes: 0,
    assessments: 0,
    points: 0,
    supplyTerritory,
    supplyShares,
    demandByTerritory: {},
    pointsByTerritory: {},
  };

  // Supply: real shifts, or a synthetic weekday shift for a sandbox-added clinician.
  let shiftMinutes = 0;
  let overflowMinutes = 0;
  const shifts = shiftsOn(state.server.shifts, providerId, dateKey, tz);
  if (shifts.length > 0) {
    for (const s of shifts) {
      const m = overlapMinutes(s.startIso, s.endIso, start, end);
      shiftMinutes += m;
      if (s.overflow) overflowMinutes += m;
    }
  } else if (isSandboxAdded(state, entry.handle) && isoWeekday(dateKey) <= 5) {
    shiftMinutes = 8 * 60;
  }
  if (shiftMinutes === 0) {
    // No shift, but a real day may still carry visits (a PTO remainder, a per diem covering ad hoc): count them.
    if (state.meta.realDays.includes(dateKey)) {
      const facts = realDemand(state, providerId, start, end, entry);
      return { ...empty, isReal: true, ...facts };
    }
    return empty;
  }

  // Blocks on that provider-day.
  const socIds = socVisitTypeIds(state.meta.bindings);
  let blockMinutes = 0;
  let reservedSocBlockMinutes = 0;
  for (const b of state.server.blocks) {
    if (b.canceled || b.providerId !== providerId) continue;
    const m = overlapMinutes(b.startIso, b.endIso, start, end);
    if (m === 0) continue;
    if (b.visitTypeIds === null) blockMinutes += m;
    else if (b.blockType === "visit_type" && b.visitTypeIds.some((id) => socIds.includes(id))) reservedSocBlockMinutes += m;
  }

  const isReal = state.meta.realDays.includes(dateKey);
  if (isReal) {
    const facts = realDemand(state, providerId, start, end, entry);
    return {
      ...empty,
      hasShift: true,
      shiftMinutes,
      overflowMinutes,
      blockMinutes,
      reservedSocBlockMinutes,
      isReal: true,
      ...facts,
    };
  }

  const v = virtualDay(state.virtualCensus, state.config, entry, dateKey, shiftMinutes - blockMinutes);
  return {
    ...empty,
    hasShift: true,
    shiftMinutes,
    overflowMinutes,
    blockMinutes,
    reservedSocBlockMinutes,
    isReal: false,
    bookedMinutes: v.bookedMinutes,
    driveMinutes: v.driveMinutes,
    deliveredMinutes: 0,
    assessments: v.assessments,
    points: v.points,
    demandByTerritory: spread(v.bookedMinutes + v.driveMinutes, supplyShares),
    pointsByTerritory: Object.fromEntries(Object.entries(supplyShares).map(([t, share]) => [t, Math.round(v.points * share * 4) / 4])),
  };
}

function realDemand(
  state: DemoState,
  providerId: number | undefined,
  start: Date,
  end: Date,
  entry: RosterEntry,
): Pick<ClinicianDayFacts, "bookedMinutes" | "driveMinutes" | "deliveredMinutes" | "assessments" | "points" | "demandByTerritory" | "pointsByTerritory"> {
  const out = { bookedMinutes: 0, driveMinutes: 0, deliveredMinutes: 0, assessments: 0, points: 0, demandByTerritory: {} as Record<string, number>, pointsByTerritory: {} as Record<string, number> };
  if (providerId === undefined) return out;
  const fallbackTerritory = supplyTerritoriesFor(entry, state.config.branch)[0];
  // The clinician's own optimistic moves count immediately: My week's header must
  // not wait a poll to agree with the cards under it.
  for (const vr of effectiveVisitResources(state)) {
    if (vr.providerId !== providerId || vr.eventStatus === "canceled") continue;
    const m = overlapMinutes(vr.startIso, vr.endIso, start, end);
    if (m === 0) continue;
    const drive = vr.startOfShiftDriveMinutes + vr.interVisitDriveMinutes + vr.endOfShiftDriveMinutes;
    out.bookedMinutes += m;
    out.driveMinutes += drive;
    if (vr.eventStatus === "complete") out.deliveredMinutes += m;
    const kind = visitTypeKindFor(state.config, state.meta.bindings, vr.visitTypeId, vr.visitTypeName);
    if (isAssessmentKind(kind)) out.assessments += 1;
    const points = pointsFor(state.config, kind);
    out.points += points;
    const territory = territoryForZip(state.config.branch, vr.patientZip) ?? fallbackTerritory;
    out.demandByTerritory[territory] = (out.demandByTerritory[territory] ?? 0) + m + drive;
    out.pointsByTerritory[territory] = (out.pointsByTerritory[territory] ?? 0) + points;
  }
  return out;
}

export function visitResourcesOn(state: Pick<DemoState, "server" | "config">, dateKey: DateKey): VisitResourceLike[] {
  const { start, end } = dayBounds(dateKey, state.config.branch.timeZone);
  return state.server.visitResources.filter((vr) => overlapMinutes(vr.startIso, vr.endIso, start, end) > 0);
}

/** All clinician-day facts for a date range (sandbox applied). */
export function clinicianDaysInRange(state: DemoState, dateKeys: DateKey[]): ClinicianDayFacts[] {
  const entries = rosterEntries(state);
  const out: ClinicianDayFacts[] = [];
  for (const entry of entries) for (const dateKey of dateKeys) out.push(clinicianDayFacts(state, entry, dateKey));
  return out;
}
